import { ComponentChildren, Fragment, FunctionComponent, h } from 'preact';
import { Icon, IconType } from 'components/icon/icon';
import './secondary-list-item.scss';

type SecondaryListItemProps = {
  label: string;
  ariaLabel?: string;
  leadingIcon?: ComponentChildren;
  chip?: string;
  trailingIcon?: IconType;
  disabled?: boolean;
  interactive?: boolean;
  standalone?: boolean;
  href?: string;
  onClick?: () => void;
  testId?: string;
  className?: string;
};

const getDefaultTrailingIcon = (chip?: string): IconType =>
  chip ? 'chevron-right-v2' : 'external-link-icon';

export const SecondaryList: FunctionComponent = ({ children }) => (
  <div class="uwaw-secondary-list__nav">{children}</div>
);

export const SecondaryListItem: FunctionComponent<SecondaryListItemProps> = ({
  label,
  ariaLabel,
  leadingIcon,
  chip,
  trailingIcon,
  disabled = false,
  interactive = true,
  standalone = false,
  href,
  onClick,
  testId,
  className = '',
}) => {
  const icon = trailingIcon ?? getDefaultTrailingIcon(chip);
  const itemClassName = [
    'uwaw-secondary-list__item',
    interactive
      ? 'uwaw-secondary-list__item--interactive'
      : 'uwaw-secondary-list__item--non-interactive',
    standalone ? 'uwaw-secondary-list__item--standalone' : '',
    disabled ? 'uwaw-secondary-list__item--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      <span class="uwaw-secondary-list__label">
        {leadingIcon}
        {label}
      </span>
      <span class="uwaw-secondary-list__actions">
        {chip && <span class="uwaw-secondary-list__chip">{chip}</span>}
        <span class="uwaw-secondary-list__trailing">
          <Icon type={icon} />
        </span>
      </span>
    </>
  );

  if (interactive && href !== undefined) {
    return (
      <a
        href={href}
        class={itemClassName}
        onClick={
          onClick
            ? (e) => {
                e.preventDefault();
                onClick();
              }
            : undefined
        }
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {content}
      </a>
    );
  }

  if (interactive) {
    return (
      <button
        type="button"
        class={itemClassName}
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {content}
      </button>
    );
  }

  return (
    <div class={itemClassName} data-testid={testId}>
      {content}
    </div>
  );
};
